import type { FC } from 'react'
import React, { memo, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import { Button } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { TextInput } from '@/components/ui/ReactHookFormFields'
import { getUser } from '@/logic/auth/reducer'
import { useData } from '@/logic/data'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { useGtm } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmLabel } from '@/logic/metrika/financing/types'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType } from '@/types'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString, normalizePhoneNumber } from '@/utils/formatUtils'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { ConsultingRequestSent } from './components/ConsultingRequestSent'
import { CONSULTING_FORM_ID } from './constants'
import { messages } from './messages'
import type { IMiniConsultingFormProps, IMiniConsultingFormValues } from './types'
import { createRequestBody } from './utils'
import './styles.scss'

const mapState = (state: RootState) => ({
  user: getUser()(state),
})

const MiniConsultingFormComponent: FC<IMiniConsultingFormProps> = props => {
  const { productId } = props
  const { user } = useSelector(mapState)

  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const { pushGtmProduct } = useGtm()
  const { data: legalDocuments = [] } = useData<TLegalDocuments>('legalDocuments')
  const agreementLink = getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)

  const { applyFinancingOffer, isLoading, isSuccess } = useApplyFinancingOffer()

  const {
    setOptions,
    profile,
    isSuccess: isBackgroundRequestSuccess,
  } = useApplyingFinancingOfferForm({
    productId: FundingProduct.MARKETPLACE,
    offerType: EFinancingOfferType.MARKETPLACE,
  })

  const initialValues = useMemo<IMiniConsultingFormValues>(
    () => ({
      productId: FundingProduct.MARKETPLACE,
      phone: profile.phone,
    }),
    [profile.phone],
  )

  const { control, handleSubmit, setValue } = useForm<IMiniConsultingFormValues>({ defaultValues: initialValues })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue('phone', normalizePhoneNumber(profile.phone)), [profile.phone])

  const onFormSubmit = handleSubmit(async values => {
    const body = createRequestBody(values, FundingProduct.MARKETPLACE, user)

    pushGtmProduct(productId, EGtmLabel.ACTION)

    if (!isAuthenticated) {
      openAuthModal({
        shouldSendCodeImmediately: true,
        shouldSkipSelectRoleStep: true,
        initialValues: { phone: normalizePhoneNumber(values.phone) },
        onCloseDialog: () => setOptions({ formValues: body }),
      })

      return
    }

    if (isApplyingOfferRequestBody(body)) {
      applyFinancingOffer({ body })
    }
  })

  if (isSuccess || isBackgroundRequestSuccess) {
    return (
      <div id="consultingOfferForm" className="miniConsultingForm">
        <ConsultingRequestSent />
      </div>
    )
  }

  return (
    <div id={CONSULTING_FORM_ID} className="miniConsultingForm">
      <h4>{messages.title}</h4>
      <div className="miniConsultingForm__description">{messages.description}</div>

      <form onSubmit={onFormSubmit} className="miniConsultingForm__form">
        <TextInput
          name="phone"
          type="tel"
          placeholder={messages.form.phone.placeholder}
          rules={{ required: messages.form.phone.error }}
          control={control}
          shouldBeTouchedBeforeError={false}
          isReadonly={isAuthenticated}
          isRequired
        />
        <Button
          type="submit"
          className="miniConsultingForm__button"
          modifiers={['full-width-always', 'outline']}
          loaderProps={{ isVisible: isLoading }}
        >
          {messages.button}
        </Button>
      </form>

      <div className="miniConsultingForm__agreement text_small">
        {formatLocalizedString(messages.agreement, {
          LinkToAgreement: chunks => (
            <CommonLink className="underline underline_green color_green" url={agreementLink} shouldOpenInNewWindow>
              {chunks}
            </CommonLink>
          ),
        })}
      </div>
    </div>
  )
}

export const MiniConsultingForm = memo(MiniConsultingFormComponent)
